'use client';

import clsx from 'clsx';
import type { ReactNode } from 'react';

import type { CMSMedia } from '@/lib/cms/types';
import { cleanCmsConfigValue } from '@/lib/contentful/preview';
import { renderCollectionItem, type ContentGroupItem } from '../DoubleCard';
import { EntryEffectWrapper } from '../EntryEffectWrapper';
import { MediaWrapper } from '../MediaWrapper';
import styles from './Grid.module.scss';

type GridColumnCount = 1 | 2 | 3 | 4;
type GridColumnCountMobile = 1 | 2;

const VALID_COLUMNS = new Set([1, 2, 3, 4]);
const VALID_COLUMNS_MOBILE = new Set([1, 2]);

function resolveGridColumnCount(raw: GridColumnCount | string | undefined): GridColumnCount {
  if (typeof raw === 'number' && VALID_COLUMNS.has(raw)) return raw;
  if (typeof raw === 'string') {
    const cleaned = cleanCmsConfigValue(raw);
    const parsed = Number(cleaned);
    if (VALID_COLUMNS.has(parsed)) return parsed as GridColumnCount;
  }
  return 3;
}

function resolveGridColumnCountMobile(
  raw: GridColumnCountMobile | string | undefined
): GridColumnCountMobile {
  if (typeof raw === 'number' && VALID_COLUMNS_MOBILE.has(raw)) return raw;
  if (typeof raw === 'string') {
    const cleaned = cleanCmsConfigValue(raw);
    const parsed = Number(cleaned);
    if (VALID_COLUMNS_MOBILE.has(parsed)) return parsed as GridColumnCountMobile;
  }
  return 1;
}

export interface GridProps {
  /** Number of columns on desktop */
  columns?: 1 | 2 | 3 | 4;
  /** Number of columns on mobile */
  columnsMobile?: 1 | 2;
  /** Gap between items */
  gap?: 'xs' | 'sm' | 'md' | 'lg';
  /** Enable slider mode on mobile */
  slider?: boolean;
  /** Reverse display order on mobile */
  reverseMobile?: boolean;
  /** Max width constraint */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Content alignment */
  align?: 'start' | 'center' | 'end';
  /** Background color */
  backgroundColor?: string;
  /** Optional cover-style background asset (image or video) for the grid.
   *  Sits above the background colour and below the content. Link/hover
   *  behaviour is intentionally suppressed — this is a backdrop, not a CTA. */
  backgroundMedia?: CMSMedia | null;
  /** Padding */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** CMS padding-top override (none, small, medium, large) */
  paddingTop?: string | null;
  /** CMS padding-bottom override (none, small, medium, large) */
  paddingBottom?: string | null;
  /** Children elements (takes precedence over contentCollection) */
  children?: ReactNode;
  /** CMS content collection (alternative to children) */
  contentCollection?: {
    items: Array<ContentGroupItem | null>;
  } | null;
  /** Extra data record for CMS hydration */
  extraDataRecord?: Record<string, unknown>;
  /** Anchor ID for navigation */
  id?: string | null;
  /** Visibility on desktop */
  visibleInDesktop?: boolean | null;
  /** Visibility on mobile */
  visibleInMobile?: boolean | null;
  /** Optional className */
  className?: string;
  /** CMS component index for E2E test targeting */
  cmsIndex?: number;
  /** Vertical alignment of grid items within their cells */
  verticalAlignment?: 'top' | 'center' | 'bottom' | null;
  /** Layout variant override */
  variant?: 'flushLeft' | null;
  /** Whether the Grid has an entry effect (slide up + opacity on scroll) */
  entryEffect?: boolean;
}

/**
 * Flexible grid layout component
 * Supports both children-based and CMS data-based rendering.
 *
 * Can contain Card components, ProductCards, or any other content.
 * Based on Breitling's Grid pattern with CMS integration.
 */
export function Grid({
  columns = 3,
  columnsMobile = 1,
  gap = 'md',
  slider = false,
  reverseMobile = false,
  maxWidth = 'xl',
  align = 'start',
  backgroundColor,
  backgroundMedia,
  padding = 'md',
  paddingTop,
  paddingBottom,
  children,
  contentCollection,
  extraDataRecord,
  id,
  visibleInDesktop,
  visibleInMobile,
  className,
  cmsIndex,
  verticalAlignment,
  variant,
  entryEffect = false,
}: GridProps) {
  const resolvedColumns = resolveGridColumnCount(columns);
  const resolvedColumnsMobile = resolveGridColumnCountMobile(columnsMobile);

  // Generate testid prefix for E2E tests
  const testIdPrefix = cmsIndex !== undefined ? `grid-${cmsIndex}` : undefined;

  // Determine what to render
  let content: ReactNode = children;

  // If no children but have CMS data, render from collection
  if (!children && contentCollection?.items) {
    const items = contentCollection.items.filter(
      (item): item is ContentGroupItem => item !== null && 'sys' in item
    );

    content = items.map((item, index) => {
      const enrichedItem =
        verticalAlignment && item.__typename === 'Card'
          ? {
              ...item,
              verticalAlignment: item.verticalAlignment || verticalAlignment,
              componentFullHeight: true,
            }
          : item;
      const rendered = renderCollectionItem(enrichedItem, extraDataRecord);
      if (!rendered) return null;

      const childHasEffect = (item as { entryEffect?: boolean }).entryEffect ?? false;
      const wrapChild = !entryEffect && childHasEffect;

      return (
        <div
          key={item.sys.id}
          className={clsx(styles.item, verticalAlignment && styles.itemFullHeight)}
          style={{ '--entry-stagger-index': index } as React.CSSProperties}
        >
          {wrapChild ? (
            <EntryEffectWrapper enabled variant="standard">
              {rendered}
            </EntryEffectWrapper>
          ) : (
            rendered
          )}
        </div>
      );
    });
  }

  // Handle visibility
  const visibilityClass = clsx(
    visibleInDesktop === false && styles.hideDesktop,
    visibleInMobile === false && styles.hideMobile
  );

  const hasBackgroundMedia = Boolean(backgroundMedia);

  return (
    <section
      id={id || undefined}
      className={clsx(
        styles.section,
        styles[`padding-${padding}`],
        paddingTop &&
          paddingTop.toLowerCase() !== 'default' &&
          styles[`paddingTop-${paddingTop.toLowerCase()}`],
        paddingBottom &&
          paddingBottom.toLowerCase() !== 'default' &&
          styles[`paddingBottom-${paddingBottom.toLowerCase()}`],
        hasBackgroundMedia && styles.hasBackgroundMedia,
        visibilityClass,
        className
      )}
      style={backgroundColor ? { backgroundColor } : undefined}
      data-testid={testIdPrefix}
    >
      {hasBackgroundMedia && (
        <div className={styles.backgroundMedia} aria-hidden="true">
          <MediaWrapper media={backgroundMedia} mediaType="Grid" fill disableLink sizes="100vw" />
        </div>
      )}
      <div className={clsx(styles.container, styles[`maxWidth-${maxWidth}`])}>
        <div
          className={clsx(
            styles.grid,
            styles[`columns-${resolvedColumns}`],
            styles[`columnsMobile-${resolvedColumnsMobile}`],
            styles[`gap-${gap}`],
            styles[`align-${align}`],
            slider && styles.slider,
            reverseMobile && styles.reverseMobile
          )}
        >
          {content}
        </div>
      </div>
    </section>
  );
}

/**
 * Grid item wrapper for consistent sizing
 */
export function GridItem({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx(styles.item, className)}>{children}</div>;
}

Grid.Item = GridItem;
Grid.displayName = 'Grid';
